import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchUserOrders } from '../../redux/slices/orderSlice';

const MyOrdersPage = () => {
const navigate = useNavigate();
const dispatch = useDispatch();
const {orders, loading, error} = useSelector((state) => state.orders);

useEffect(() => {
    dispatch(fetchUserOrders());
}, [dispatch]);

const handleRowClick = (orderId) => {
    navigate(`/order/${orderId}`);
};

if (loading) return (
    <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">Loading orders...</span>
    </div>
);

if (error) return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <strong>Error:</strong> {error}
        </div>
    </div>
);

return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold mb-6">
            My Orders
        </h2>

        {/* Desktop Table View */}
        <div className="hidden md:block relative shadow-md sm:rounded-lg overflow-hidden">
            <table className="min-w-full text-left text-gray-500">
                <thead className="bg-gray-100 text-xs uppercase text-gray-700">
                    <tr>
                        <th className="py-3 px-4">Image</th>
                        <th className="py-3 px-4">Order ID</th>
                        <th className="py-3 px-4">Created</th>
                        <th className="py-3 px-4">Shipping Address</th>
                        <th className="py-3 px-4">Items</th>
                        <th className="py-3 px-4">Price</th>
                        <th className="py-3 px-4">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.length > 0 ? (
                        orders.map((order) => (
                            <tr key={order._id} onClick={() => handleRowClick(order._id)} className="border-b hover:bg-gray-50 cursor-pointer">
                                <td className="py-4 px-4">
                                    <img src={order.orderItems[0].image} alt={order.orderItems[0].name} 
                                    className="w-12 h-12 object-cover rounded-lg" />
                                </td>
                                <td className="py-4 px-4 font-medium text-gray-900 whitespace-nowrap">#{order._id}</td>
                                <td className="py-4 px-4">
                                    {new Date(order.createdAt).toDateString()}{" "}
                                    {new Date(order.createdAt).toLocaleTimeString()}
                                </td>
                                <td className="py-4 px-4">
                                    {order.shippingAddress ? `${order.shippingAddress.address}, ${order.shippingAddress.state} ${order.shippingAddress.country}`: "N/A"}
                                </td>
                                <td className="py-4 px-4">{order.orderItems.length}</td>
                                <td className="py-4 px-4">₹{order.totalPrice}</td>
                                <td className="py-4 px-4">
                                    <span className={`${order.isPaid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"} px-2 py-1 rounded-full text-sm font-medium`}>
                                        {order.isPaid ? "Paid" : "Pending"}
                                    </span>
                                </td>
                            </tr>
                        ))
                    ): (
                        <tr>
                            <td colSpan={7} className="py-4 px-4 text-center text-gray-500">
                                You Have No Orders
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
            {orders.length > 0 ? (
                orders.map((order) => (
                    <div 
                        key={order._id} 
                        onClick={() => handleRowClick(order._id)}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    >
                        {/* Order Header */}
                        <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                            <div>
                                <p className="text-xs text-gray-500">Order ID</p>
                                <p className="font-medium text-gray-900 text-sm">#{order._id}</p>
                            </div>
                            <span className={`${order.isPaid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"} px-3 py-1 rounded-full text-xs font-medium`}>
                                {order.isPaid ? "Paid" : "Pending"}
                            </span>
                        </div>

                        {/* Order Content */}
                        <div className="p-4">
                            {/* Product Image and Items Count */}
                            <div className="flex items-center gap-3 mb-3">
                                <img 
                                    src={order.orderItems[0].image} 
                                    alt={order.orderItems[0].name} 
                                    className="w-16 h-16 object-cover rounded-lg"
                                />
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">
                                        {order.orderItems[0].name}
                                    </p>
                                    {order.orderItems.length > 1 && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            +{order.orderItems.length - 1} more item(s)
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Order Details */}
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Items:</span>
                                    <span className="font-medium text-gray-900">{order.orderItems.length}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Price:</span>
                                    <span className="font-semibold text-gray-900">₹{order.totalPrice}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Order Date:</span>
                                    <span className="text-gray-900 text-xs">
                                        {new Date(order.createdAt).toLocaleDateString('en-IN')}
                                    </span>
                                </div>
                                {order.shippingAddress && (
                                    <div className="pt-2 border-t">
                                        <p className="text-xs text-gray-500 mb-1">Shipping To:</p>
                                        <p className="text-xs text-gray-700">
                                            {order.shippingAddress.address}, {order.shippingAddress.city}, {order.shippingAddress.state}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* View Details Arrow */}
                        <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-t">
                            <span className="text-xs text-gray-600">Tap to view details</span>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>
                ))
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    <p className="text-gray-500 text-lg mb-2">You Have No Orders</p>
                    <p className="text-gray-400 text-sm">Your orders will appear here once you make a purchase</p>
                </div>
            )}
        </div>
    </div>
  )
}

export default MyOrdersPage
